<template>
    <page :busy="busy">
        <div v-show="isNil(packageID)">
            <b-row class="m-1 mt-2">
                <b-col md="12">
                    <h5><b>Please select how many properties you manage or own.</b></h5>
                </b-col>
            </b-row>
            <grid
                :column-defs="columnDefs"
                :is-auto-height="true"
                :is-search="false"
                :row-data="packages"
                :get-row-height="getRowHeight"
            >
            </grid>
        </div>
        <div v-show="!isNil(packageID)">
            <b-row>
                <b-col md="12" class="modules-text">        
                    <h6>Modules can be added or removed at any time.   Billing is prorated for the number of days remaining in the current month.</h6>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="12" class="mb-3">
                    <grid
                        ref="modulesGrid"
                        :column-defs="columnDefsModules"
                        :isAutoHeight="true"
                        :isSearch="false"
                        :row-data="modules"
                    >
                    </grid>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="12">
                    <b-button-group size="md" class="bottom-buttons">
                        <b-button variant="default" @click="handleBackButton"
                            ><font-awesome-icon :icon="['fas', 'arrow-left']"></font-awesome-icon> Back</b-button
                        >
                        <b-button variant="primary" @click="handleFinishButton">Subscribe</b-button>
                    </b-button-group>
                </b-col>
            </b-row>
        </div>
        <modal id="cancellation-message-modal" title="Subscription Canceled" ok-only size="lg">
            <p>
                Per your request, your Lodgix.com subscription has been cancelled. If you cancelled before your trial
                expired your credit card will not be billed. Per our Terms and Conditions, we do not prorate accounts.
                An email has been sent to you confirming your account cancellation
            </p>
            <p>
                If at any time in the next 12 months, you would like to restart your subscription, you can login to the
                Lodgix.com application and restart your subscription
            </p>
        </modal>
    </page>
</template>

<script>
    import { mapState } from 'vuex';
    import isNil from 'lodash/isNil';
    import isEmpty from 'lodash/isEmpty';
    import page from '../../generic/page/page.vue';
    import API from '../../../api';
    import grid from '../../generic/grid/grid.vue';
    import Modal from '../../generic/modal/modal.vue';
    import ModulesActionsCellRenderer from '../common/modules-actions-cell-renderer.vue';
    import FormSquareSwitch from '../../generic/form-square-switch/form-square-switch.vue';
    import { formatResponseError } from '../../../utils/api';
    import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import { SUBSCRIPTION_SELECT_DATA_ENDPOINT, SUBSCRIPTION_SELECT_SUBSCRIBE_ENDPOINT } from '../../../endpoints';

    export default {
        components: {
            page,
            grid,
            FormSquareSwitch,
            Modal,
            actionsCellRenderer: ActionsCellRenderer,
            modulesActionsCellRenderer: ModulesActionsCellRenderer,
        },
        mixins: [removeScrollMixin],
        mounted() {
            this.modulesGridApi = this.$refs.modulesGrid.gridOptions.api;
            this.fetchSelectData();
            if (this.$store.state.subscription.showCancelationMessage) {
                this.$store.state.subscription.showCancelationMessage = false;
                this.$bvModal.show('cancellation-message-modal');
            }
        },
        data() {
            return {
                busy: false,
                packageID: null,
                packages: [],
                modules: [],
                selectedModules: {},
                modulesGridApi: null,
            };
        },
        computed: {
            columnDefs() {
                return [
                    {
                        headerName: 'Package',
                        field: 'name',
                        minWidth: 120,
                        maxWidth: 120,
                        sortable: false,
                    },
                    {
                        headerName: 'Description',
                        field: 'description',
                        sortable: false,
                        minWidth: 350,
                    },
                    {
                        headerName: 'Price',
                        field: 'amount',
                        minWidth: 150,
                        maxWidth: 300,
                        sortable: false,
                        cellRenderer: ({ value }) => {
                            return `${value} / mo.`;
                        },
                    },
                    {
                        headerName: '',
                        minWidth: 170,
                        maxWidth: 170,
                        cellRenderer: 'actionsCellRenderer',
                        cellRendererParams: {
                            onSelect: async packageID => {
                                this.modules = this.packages.find(obj => {
                                    return obj.id === packageID;
                                }).modules;
                                this.selectedModules = {};
                                this.modules.forEach((item, i) => {
                                    if (item.component == 'website') {
                                        this.selectedModules[item.id] = true;
                                    } else {
                                        this.selectedModules[item.id] = false;
                                    }
                                });
                                this.packageID = packageID;
                            },
                        },
                        sortable: false,
                    },
                ];
            },
            columnDefsModules() {
                return [
                    {
                        field: 'description',
                        headerName: 'Description',
                        sortable: false,
                        resizable: false,
                        minWidth: 250,
                    },
                    {
                        field: 'max_properties',
                        headerName: 'Max. Properties',
                        resizable: false,
                        sortable: false,
                        minWidth: 130,
                    },
                    {
                        field: 'amount',
                        headerName: 'Price',
                        sortable: false,
                        resizable: false,
                        minWidth: 80,
                    },
                    {
                        field: 'billing_period_string',
                        headerName: 'Billing',
                        sortable: false,
                        resizable: false,
                        minWidth: 90,
                    },
                    {
                        headerName: '',
                        minWidth: 110,
                        maxWidth: 110,
                        sortable: false,
                        resizable: false,
                        cellRenderer: 'modulesActionsCellRenderer',
                        cellRendererParams: {
                            selectedModulesGetter: () => this.selectedModules,
                            onToggleModule: async (value, module) => {
                                this.toggleModule(value, module);
                            },
                        },
                    },
                ];
            },
        },
        methods: {
            isNil,
            async fetchSelectData() {
                try {
                    this.busy = true;
                    const { data } = await API.get(SUBSCRIPTION_SELECT_DATA_ENDPOINT());
                    this.packages = data.packages;
                } catch (e) {
                    this.errorToast({ text: 'Could not load subscription select data.' });
                } finally {
                    this.busy = false;
                }
            },
            async toggleModule(value, module) {
                this.selectedModules[module.id] = value;
                this.modulesGridApi.refreshCells({ force: true });
            },
            handleBackButton() {
                this.packageID = null;
            },
            async handleFinishButton() {
                this.busy = true;
                try {
                    const selectedModules = this.selectedModules;
                    const modules = Object.keys(selectedModules)
                        .filter(function(k) {
                            return selectedModules[k];
                        })
                        .map(Number);
                    const response = await API.post(SUBSCRIPTION_SELECT_SUBSCRIBE_ENDPOINT(), {
                        package_id: this.packageID,
                        modules: modules,
                    });
                    if (response.data.status == 'TRIAL') {
                        window.location = '/app/dashboard';
                    } else {
                        this.$router.push('/app/subscription/billing/');
                    }
                } catch (error) {
                    console.log(error);
                    const { generic } = formatResponseError(error);
                    this.errorToast({
                        text: generic || 'Something went wrong while creating subscription.',
                    });
                } finally {
                    this.busy = false;
                }
            },
            getRowHeight() {
                return 45;
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    :deep(.modules-text) {
        margin: 1rem;
        margin-left: 1.2rem !important;
        font-size: 14px !important;
    }

    :deep(.modules-text h6) {
        font-size: 15px !important;
    }

    :deep(.bottom-buttons) {
        margin-left: 5px;
    }

    :deep(.ag-cell-wrapper) {
        display: block !important;
    }
</style>
